import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarRange, Columns3, Newspaper, Plus, Trash2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PageHeader, Panel, Empty } from "@/components/PageHeader";
import { FlyerImport } from "@/components/flyers/FlyerImport";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fdate } from "@/lib/api";

export default function Flyers() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [pick, setPick] = useState([]);
  const canWrite = user?.permissions?.flyers === "write";
  const load = () => api.get("/flyers").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const del = async (f) => {
    if (!window.confirm(`Eliminare il volantino "${f.name}"?`)) return;
    try { await api.delete(`/flyers/${f.id}`); toast.success("Volantino eliminato"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="flyers-page">
      <PageHeader title="Analisi volantini" subtitle="Importa un volantino e misura vendite, prezzi effettivi e risposta dei punti vendita">
        <Button variant="outline" data-testid="flyer-compare-button" disabled={pick.length < 2 || pick.length > 6} onClick={() => nav(`/volantini-promo/confronto?ids=${pick.join(",")}`)}>
          <Columns3 className="mr-1 h-4 w-4" />Confronta{pick.length ? ` (${pick.length})` : ""}</Button>
        {canWrite && <Button data-testid="flyer-new-button" className="bg-blue-600 hover:bg-blue-700" onClick={() => setOpen(true)}><Plus className="mr-1 h-4 w-4" />Nuovo volantino</Button>}
      </PageHeader>
      <Panel testId="flyers-list">
        {rows && rows.length > 1 && <p className="border-b border-slate-100 px-5 py-2 text-xs text-slate-400">Seleziona da 2 a 6 volantini per confrontarli</p>}
        {rows && !rows.length && <Empty text="Nessun volantino ancora: importa il primo da Excel o CSV" />}
        <div className="divide-y divide-slate-100">
          {(rows || []).map((f) => (
            <div key={f.id} className="flex flex-wrap items-center gap-4 px-5 py-4 transition-colors hover:bg-slate-50" data-testid={`flyer-row-${f.id}`}>
              <Checkbox data-testid={`flyer-pick-${f.id}`} checked={pick.includes(f.id)} onCheckedChange={(v) => setPick(v ? [...pick, f.id] : pick.filter((x) => x !== f.id))} />
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-blue-600"><Newspaper className="h-5 w-5" /></div>
              <button type="button" className="min-w-[200px] flex-1 basis-[calc(100%-6rem)] text-left sm:basis-0" data-testid={`flyer-open-${f.id}`} onClick={() => nav(`/volantini-promo/${f.id}`)}>
                <div className="truncate font-medium text-slate-900">{f.name}</div>
                <div className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-500"><CalendarRange className="h-3.5 w-3.5" />{fdate(f.start)} – {fdate(f.end)} · {f.items_count} prodotti</div>
              </button>
              <div className="flex flex-wrap gap-1.5 text-[11px]">
                <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-emerald-700">{f.matches.ean + f.matches.manual} abbinati</span>
                {f.matches.descr > 0 && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-amber-700">{f.matches.descr} da verificare</span>}
                {f.matches.none > 0 && <span className="rounded-full bg-rose-50 px-2 py-0.5 text-rose-700">{f.matches.none} non trovati</span>}
              </div>
              {canWrite && <Button size="sm" variant="ghost" data-testid={`flyer-delete-${f.id}`} onClick={() => del(f)}><Trash2 className="h-4 w-4 text-slate-400" /></Button>}
            </div>))}
        </div>
      </Panel>
      <FlyerImport open={open} onClose={() => { setOpen(false); load(); }} />
    </div>
  );
}
